import { action } from '@ember/object';
import Component from '@glimmer/component';
import type { Sprite } from 'ember-animated';
import animatedIf from 'ember-animated/components/animated-if';
import { fadeIn } from 'ember-animated/motions/opacity';
import RecurringInvites from 'garaje/components/invites/recurring-invites';
import RecurringInvitesEnd from 'garaje/components/invites/recurring-invites-end';
import RecurringRule from 'garaje/models/recurring-rule';
import type DateLimits from 'garaje/types/date-time/date-limits';
import _compact from 'lodash/compact';
import { cached } from 'tracked-toolbox';

export type RecurringOption = {
  display: string;
  value: string;
};

interface RecurringInvitesContainerSignature {
  Args: {
    expectedArrivalTime?: Date | null;
    disabled?: boolean;
    minDate?: Date | null;
    onChange: (value: string) => void;
    selected?: string;
    timezone?: string;
    isReadonly?: boolean;
    error?: string;
    isEditInvite?: boolean;
    disabledFromMultilocation?: boolean;
    dateLimits?: DateLimits;
    shortcuts?: string[];
    isRecurringGroupInvite?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * Form with dropdown and date-picker for managing recurring invites
 */
export default class RecurringInvitesContainer extends Component<RecurringInvitesContainerSignature> {
  @cached
  get recurringRule(): RecurringRule {
    if (this.args.selected) {
      return RecurringRule.parse(this.args.selected);
    } else {
      return new RecurringRule();
    }
  }

  get showRepeatEnd(): boolean {
    return !!this.recurringRule.frequency;
  }

  get recurringRuleWithoutUntil(): string {
    let props;
    if (this.args.disabledFromMultilocation) {
      props = undefined;
    } else {
      props = {
        frequency: this.recurringRule.frequency,
        byDay: this.recurringRule.byDay,
        byMonth: this.recurringRule.byMonth,
        byMonthDay: this.recurringRule.byMonthDay,
      };
    }

    return new RecurringRule(props).toString();
  }

  /* eslint-disable require-yield */
  @action
  *focusAfterTransition({ insertedSprites }: { insertedSprites: Sprite[] }): Generator<never, void, unknown> {
    if (this.args.isEditInvite) {
      insertedSprites.forEach((sprite) => {
        sprite.reveal();
      });
      return;
    }

    insertedSprites.forEach((sprite) => {
      void fadeIn(sprite);
    });

    const container = insertedSprites[0]?.element;
    if (!container) return;

    const datePicker = container.querySelector<HTMLElement>('#rruleUntil');
    if (datePicker) datePicker.focus();
  }

  @action
  onRruleSelect(option: RecurringOption): void {
    const recurringRule = RecurringRule.parse(option.value);

    // NOTE if user selects 'Does not repeat', we do not want to update the until
    if (!recurringRule.isBlank()) {
      recurringRule.until = this.recurringRule.until;
    }

    this.args.onChange(recurringRule.toString());
  }

  @action
  onRruleUntilSelect(date?: Date): void {
    if (date) {
      this.recurringRule.until = date;
    } else {
      this.recurringRule.until = null;
    }

    this.args.onChange(this.recurringRule.toString());
  }

  <template>
    <div class="flow-root mb-2" ...attributes>
      <div class="float-left box-border w-6/12 pr-4">
        <label for="rrule-select">Repeats</label>
        <RecurringInvites
          @disabled={{@disabled}}
          @expectedArrivalTime={{@expectedArrivalTime}}
          @onChange={{this.onRruleSelect}}
          @selected={{this.recurringRuleWithoutUntil}}
          @isReadonly={{@isReadonly}}
        />
      </div>

      <div class="float-left box-border w-6/12 pl-2">
        {{#unless @disabled}}
          {{#animatedIf this.showRepeatEnd use=this.focusAfterTransition}}
            <RecurringInvitesEnd
              @timezone={{@timezone}}
              @onChange={{this.onRruleUntilSelect}}
              @value={{this.recurringRule.until}}
              @isReadonly={{@isReadonly}}
              @error={{@error}}
              @isEditInvite={{@isEditInvite}}
              @dateLimits={{@dateLimits}}
              @shortcuts={{@shortcuts}}
              @isRecurringGroupInvite={{@isRecurringGroupInvite}}
            />
          {{/animatedIf}}
        {{/unless}}
      </div>
    </div>
  </template>
}
